"use client";

import { Event, Group } from "@/db/schema";
import { cn } from "@/lib/utils";
import { CalendarClock, TriangleAlert } from "lucide-react";
import { useMemo } from "react";

export function CalendarEventBlock({
  event,
  group,
  onClick,
}: {
  event: Event & { syncStatus?: string | null };
  group: Group;
  onClick: () => void;
}) {
  const colors = useMemo(() => {
    switch (group.color) {
      case "blue":
        return {
          bg: "bg-blue-25",
          text: "text-blue-125",
          hoverBg: "hover:bg-blue-25/75",
        };
      case "green":
        return {
          bg: "bg-green-25",
          text: "text-green-125",
          hoverBg: "hover:bg-green-25/75",
        };
      case "yellow":
        return {
          bg: "bg-yellow-25",
          text: "text-yellow-125",
          hoverBg: "hover:bg-yellow-25/75",
        };
      case "orange":
        return {
          bg: "bg-orange-25",
          text: "text-orange-125",
          hoverBg: "hover:bg-orange-25/75",
        };
      case "red":
        return {
          bg: "bg-red-25",
          text: "text-red-125",
          hoverBg: "hover:bg-red-25/75",
        };
      case "pink":
        return {
          bg: "bg-pink-25",
          text: "text-pink-125",
          hoverBg: "hover:bg-pink-25/75",
        };
      case "purple":
        return {
          bg: "bg-purple-25",
          text: "text-purple-125",
          hoverBg: "hover:bg-purple-25/75",
        };
      case "cyan":
        return {
          bg: "bg-cyan-25",
          text: "text-cyan-125",
          hoverBg: "hover:bg-cyan-25/75",
        };
      default:
        return {
          bg: "bg-slate-20",
          text: "text-slate-100",
          hoverBg: "hover:bg-slate-10",
        };
    }
  }, [group.color]);

  const timeIsOneCell =
    new Date(event.endDate).getTime() - new Date(event.startDate).getTime() <= 30 * 60 * 1000;

  const conflict = event.syncStatus === "conflict";
  const fromOutlook = event.source === "outlook";

  return (
    <div
      onClick={onClick}
      className={cn(
        "flex w-full rounded-md p-2 md:p-3 text-xs overflow-hidden h-full cursor-pointer text-start",
        timeIsOneCell ? "flex-row gap-2 items-center" : "flex-col",
        colors.bg,
        colors.hoverBg,
        colors.text,
        // A sync conflict must be visible even when another booking paints
        // over this one in the grid — ring + amber tint beat the group color.
        conflict && "ring-2 ring-amber-500 bg-amber-50 text-amber-900",
      )}
    >
      <span className="font-semibold flex items-center gap-1">
        {conflict && <TriangleAlert className="h-3 w-3 shrink-0 text-amber-600" />}
        {!conflict && fromOutlook && <CalendarClock className="h-3 w-3 shrink-0 opacity-60" />}
        {new Date(event.startDate).toLocaleTimeString("sv-SE", {
          hour: "2-digit",
          minute: "2-digit",
        })}{" "}
        -{" "}
        {new Date(event.endDate).toLocaleTimeString("sv-SE", {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </span>
      <span className="max-w-full truncate">
        {conflict && <span className="font-semibold">Double-booked: </span>}
        {event.title}
      </span>
      {!timeIsOneCell && <span className="max-w-full truncate">{event.authorCompany}</span>}
    </div>
  );
}
